// modules/BattleLogic.js
import { GAME_CONSTANTS } from './GameConstants.js';
import DebugHelper from './DebugHelper.js';
import VehicleEquipmentSystem from './VehicleEquipmentSystem.js';

export default class BattleLogic {
    constructor(scene) {
        this.scene = scene;
        this.enemies = [];
        this.events = new Phaser.Events.EventEmitter();
        
        // 战斗配置（使用常量）
        this.config = {
            baseCriticalChance: GAME_CONSTANTS.BATTLE.BASE_CRITICAL_CHANCE,
            criticalMultiplier: GAME_CONSTANTS.BATTLE.CRITICAL_MULTIPLIER,
            escapeChance: GAME_CONSTANTS.BATTLE.ESCAPE_CHANCE,
            minDamage: GAME_CONSTANTS.BATTLE.MIN_DAMAGE,
            damageVariation: GAME_CONSTANTS.BATTLE.DAMAGE_VARIATION
        };
    }

    init(data) {
        this.enemies = data.enemies || [];
        this.encounterType = data.encounterType || 'random';
    }

  
    calculatePlayerDamage(player, target, weaponSlot = null) {
        // 检查是否在战车中
        const inVehicle = player.inVehicle && player.vehicle;
        
        if (inVehicle) {
            // 战车战斗
            return this.calculateVehicleDamage(player, target, weaponSlot);
        } else {
            // 人类战斗
            return this.calculateHumanDamage(player, target);
        }
    }
    
    /**
     * 计算人类伤害
     */
    calculateHumanDamage(player, target) {
        // 获取玩家总攻击力
        let totalAttack;
        
        if (typeof player.getAttackPower === 'function') {
            // 如果player有getAttackPower方法，使用它
            totalAttack = player.getAttackPower();
        } else if (this.scene && this.scene.battlePlayer && typeof this.scene.battlePlayer.getAttackPower === 'function') {
            // 如果场景中有battlePlayer实例，使用它的getAttackPower方法
            totalAttack = this.scene.battlePlayer.getAttackPower();
        } else {
            // 否则计算总攻击力（使用实际角色攻击力，不硬编码）
            const baseAttack = player.attack || 0;
            const weaponBonus = this.calculateWeaponDamage(player);
            totalAttack = baseAttack + weaponBonus;
        }
        
        // 获取目标防御力
        const targetDefense = target.defense || 5;
        
        // 基础伤害计算
        let damage = Math.max(this.config.minDamage, totalAttack - targetDefense);
        
        // 伤害浮动
        const variation = 1 - this.config.damageVariation + (Math.random() * this.config.damageVariation * 2);
        damage = Math.floor(damage * variation);
        
        // 暴击判断
        const isCritical = Math.random() < this.config.baseCriticalChance;
        if (isCritical) {
            damage = Math.floor(damage * this.config.criticalMultiplier);
            DebugHelper.log(`暴击！基础伤害: ${damage / this.config.criticalMultiplier}, 最终伤害: ${damage}`);
        }
        
        // 武器特效（未实现 special 仅保留伤害计算，不附加状态）
        const weapon = this.getPlayerWeapon(player);
        if (weapon && weapon.special) {
            if (GAME_CONSTANTS.BATTLE.IMPLEMENTED_WEAPON_SPECIALS.includes(weapon.special)) {
                damage = this.applyWeaponSpecial(weapon, damage, target);
            }
        }
        
        DebugHelper.log(`伤害计算: 攻击力${totalAttack} - 防御力${targetDefense} = ${damage}`);
        
        return {
            damage: damage,
            isCritical: isCritical,
            weaponEffect: weapon ? weapon.special : null
        };
    }
    
    /**
     * 计算战车伤害
     */
    calculateVehicleDamage(player, target, weaponSlot = null) {
        const vehicle = player.vehicle;
        if (!vehicle) {
            DebugHelper.warn('BattleLogic: 玩家在战车中但没有战车数据');
            return this.calculateHumanDamage(player, target);
        }
        
        // 获取战车属性
        const vehicleStats = VehicleEquipmentSystem.calculateVehicleStats(vehicle);
        
        // 选择武器
        let weapon = null;
        if (weaponSlot) {
            weapon = VehicleEquipmentSystem.getEquippedItem(vehicle, weaponSlot);
        } else {
            // 默认使用主炮，如果没有则使用副炮
            weapon = VehicleEquipmentSystem.getEquippedItem(vehicle, 'mainCannon') ||
                     VehicleEquipmentSystem.getEquippedItem(vehicle, 'subCannon');
        }
        
        if (!weapon) {
            DebugHelper.warn('BattleLogic: 战车没有可用武器');
            return {
                damage: 0,
                isCritical: false,
                weaponEffect: null,
                message: '战车没有装备武器！'
            };
        }
        
        // 检查弹药
        if (!VehicleEquipmentSystem.hasAmmo(weapon, vehicle)) {
            DebugHelper.warn(`BattleLogic: 武器 ${weapon.name} 没有弹药`);
            return {
                damage: 0,
                isCritical: false,
                weaponEffect: null,
                message: `${weapon.name} 没有弹药！`
            };
        }
        
        // 消耗弹药
        VehicleEquipmentSystem.consumeAmmo(weapon, vehicle);
        
        // 计算伤害
        const weaponDamage = weapon.damage || 0;
        const targetDefense = target.defense || 5;
        
        // 基础伤害计算
        let damage = Math.max(this.config.minDamage, weaponDamage - targetDefense);
        
        // 伤害浮动
        const variation = 1 - this.config.damageVariation + (Math.random() * this.config.damageVariation * 2);
        damage = Math.floor(damage * variation);
        
        // 暴击判断
        const isCritical = Math.random() < this.config.baseCriticalChance;
        if (isCritical) {
            damage = Math.floor(damage * this.config.criticalMultiplier);
            DebugHelper.log(`战车暴击！基础伤害: ${damage / this.config.criticalMultiplier}, 最终伤害: ${damage}`);
        }
        
        // S-E特殊效果处理
        let skillEffect = null;
        if (weapon.slot === 'se' && weapon.skillEffect) {
            skillEffect = weapon.skillEffect;
            // S-E技能效果可能影响伤害
            damage = this.applySEEffect(skillEffect, damage, target);
        }
        
        DebugHelper.log(`战车伤害计算: 武器伤害${weaponDamage} - 防御力${targetDefense} = ${damage}`);
        
        return {
            damage: damage,
            isCritical: isCritical,
            weaponEffect: skillEffect || weapon.skillEffect || null,
            weapon: weapon,
            weaponSlot: weaponSlot || weapon.slot
        };
    }
    
    /**
     * 应用S-E技能效果
     * @param {string} skillEffect - 技能效果类型
     * @param {number} baseDamage - 基础伤害
     * @param {Object} target - 目标
     * @returns {number} 最终伤害
     */
    applySEEffect(skillEffect, baseDamage, target) {
        let finalDamage = baseDamage;
        
        switch(skillEffect) {
            case 'laser_blast':
                // 激光炮：穿透效果，忽略50%防御
                const laserDefense = Math.floor((target.defense || 0) * 0.5);
                finalDamage = Math.max(this.config.minDamage, baseDamage - laserDefense);
                // 额外伤害加成
                finalDamage = Math.floor(finalDamage * 1.3);
                DebugHelper.log(`激光炮效果: 穿透防御，最终伤害 ${finalDamage}`);
                break;
                
            case 'missile_strike':
                // 导弹：范围伤害，对目标造成150%伤害
                finalDamage = Math.floor(baseDamage * 1.5);
                // 可能对周围敌人造成溅射伤害（在战斗场景中处理）
                DebugHelper.log(`导弹效果: 范围伤害，最终伤害 ${finalDamage}`);
                break;
                
            default:
                // 默认不改变伤害
                break;
        }
        
        return finalDamage;
    }
    
    /**
     * 获取战车可用的S-E技能列表
     * @param {Object} player - 玩家对象
     * @returns {Array<Object>} S-E技能列表
     */
    getVehicleSESkills(player) {
        if (!player.inVehicle || !player.vehicle) {
            return [];
        }
        
        const vehicle = player.vehicle;
        const seWeapon = VehicleEquipmentSystem.getEquippedItem(vehicle, 'se');
        
        if (!seWeapon || !seWeapon.skillEffect) {
            return [];
        }
        
        // 检查弹药
        if (!VehicleEquipmentSystem.hasAmmo(seWeapon, vehicle)) {
            return [];
        }
        
        // 返回S-E技能
        return [{
            id: seWeapon.id,
            name: seWeapon.name,
            cost: 0, // S-E使用弹药，不消耗SP
            description: seWeapon.description || 'S-E特殊攻击',
            skillEffect: seWeapon.skillEffect,
            weapon: seWeapon,
            type: 'vehicle_se'
        }];
    }

    calculateWeaponDamage(player) {
        if (!player.equipment) return 0;
        
        let weaponDamage = 0;
        
        // 查找所有装备的攻击加成（不仅仅是武器）
        player.equipment.forEach(item => {
            if (item) {
                // 计算所有装备的攻击加成
                if (item.damage) {
                    weaponDamage += item.damage;
                }
            }
        });
        
        return weaponDamage;
    }

    /**
     * 获取玩家当前武器
     */
    getPlayerWeapon(player) {
        if (!player.equipment) return null;
        
        return player.equipment.find(item => 
            item && (item.slot === 'weapon' || item.type === 'weapon')
        );
    }




    applyWeaponSpecial(weapon, baseDamage, target) {
        let finalDamage = baseDamage;
        
        switch(weapon.special) {
            case 'pierce':
                // 穿透效果：忽略部分防御
                finalDamage = Math.max(this.config.minDamage, 
                    (this.scene.battlePlayer.getAttackPower() * 0.8) - (target.defense * 0.3));
                break;
                
            case 'fire':
                finalDamage = Math.floor(baseDamage * 1.2);
                break;
                
            case 'ice':
                finalDamage = Math.floor(baseDamage * 0.9);
                break;
                
            case 'lightning':
                // 尚未实现连锁，仅轻微增伤
                finalDamage = Math.floor(baseDamage * 1.05);
                break;
                
            default:
                break;
        }
        
        return finalDamage;
    }

    calculateEnemyDamage(enemy, player) {
        // 敌人伤害计算
        const enemyAttack = enemy.attack || 8;
        const playerDefense = player.defense || 5;
        
        // 直接从装备计算防御加成
        let equipmentDefenseBonus = 0;
        if (player.equipment) {
            player.equipment.forEach(item => {
                if (item && item.defense) {
                    equipmentDefenseBonus += item.defense;
                }
            });
        }
        
        const totalDefense = playerDefense + equipmentDefenseBonus;
        
        let damage = Math.max(this.config.minDamage, enemyAttack - totalDefense);
        
        // 伤害浮动
        const variation = 0.8 + Math.random() * 0.4; // 80%-120%浮动
        damage = Math.floor(damage * variation);
        
        // 检查是否处于防御状态
        if (player.defending && player.defenseBonus) {
            // 应用防御减伤（例如减少50%伤害）
            const originalDamage = damage;
            damage = Math.floor(damage * (1 - player.defenseBonus));
            DebugHelper.log(`防御状态：伤害从 ${originalDamage} 减少到 ${damage} (减伤${(player.defenseBonus * 100).toFixed(0)}%)`);
        }
        
        // 敌人可能有特殊攻击
        if (enemy.specialAttack) {
            damage = this.applyEnemySpecial(enemy, damage, player);
        }

        // 减速：敌人造成的伤害降低
        if (enemy.slowed && enemy.slowedTurns > 0) {
            damage = Math.floor(damage * GAME_CONSTANTS.STATUS.SLOW_DAMAGE_MULTIPLIER);
        }
        
        return {
            damage: damage,
            isSpecial: !!enemy.specialAttack,
            effect: enemy.specialAttack
        };
    }

    applyEnemySpecial(enemy, baseDamage, player) {
        let finalDamage = baseDamage;
        
        switch (enemy.specialAttack) {
            case 'poison':
                finalDamage = Math.floor(baseDamage * 0.8);
                if (player) {
                    player.poisoned = true;
                    player.poisonTurns = Math.max(player.poisonTurns || 0, 3);
                }
                break;
                
            case 'stun':
                if (player && Math.random() < 0.35) {
                    player.stunned = true;
                    player.stunnedTurns = 1;
                }
                break;
                
            case 'lifeSteal': {
                finalDamage = baseDamage;
                const heal = Math.floor(baseDamage * 0.5);
                const maxHp = enemy.maxHp || enemy.hp;
                enemy.hp = Math.min(maxHp, enemy.hp + heal);
                break;
            }
                
            case 'multiHit':
                finalDamage = Math.floor(baseDamage * 0.7) + Math.floor(baseDamage * 0.7);
                break;
        }
        
        return finalDamage;
    }

    applyDamage(target, damageInfo) {
        try {
            if (!target || typeof target.hp !== 'number') return false;
            
            // 确保damageInfo是对象
            const damage = typeof damageInfo === 'number' ? damageInfo : (damageInfo.damage || 0);
            
            target.hp -= damage;
            if (target.hp < 0) target.hp = 0;
            
            // 应用额外效果
            if (damageInfo && damageInfo.weaponEffect) {
                this.applyDamageEffect(target, damageInfo.weaponEffect);
            }
            
            DebugHelper.log(`造成伤害: ${damage}, 目标剩余HP: ${target.hp}`);
            
            return target.hp <= 0; // 返回是否被击败
        } catch (error) {
            DebugHelper.error('应用伤害失败', error);
            return false;
        }
    }

    applyDamageEffect(target, effect) {
        switch(effect) {
            case 'fire':
                target.burning = true;
                target.burningTurns = GAME_CONSTANTS.STATUS.BURN_TURNS;
                break;
                
            case 'ice':
                target.slowed = true;
                target.slowedTurns = GAME_CONSTANTS.STATUS.SLOW_TURNS;
                break;
                
            case 'poison':
                target.poisoned = true;
                target.poisonTurns = 3;
                break;
        }
    }

    checkBattleStatus(player, enemies) {
        // 检查玩家是否战败
        if (player.hp <= 0) {
            return 'defeat';
        }
        
        // 检查敌人是否全灭
        const aliveEnemies = enemies.filter(enemy => 
            enemy.visible && enemy.enemyData && enemy.enemyData.hp > 0
        );
        
        if (aliveEnemies.length === 0) {
            return 'victory';
        }
        
        return 'ongoing';
    }

    attemptEscape() {
        const success = Math.random() < this.config.escapeChance;
        
        if (success) {
            DebugHelper.log('逃跑成功');
            return true;
        } else {
            DebugHelper.log('逃跑失败');
            return false;
        }
    }

    executeEnemyTurn(aliveEnemies, player, onAttackComplete) {
        if (aliveEnemies.length === 0) {
            this.events.emit('enemyTurnComplete');
            return;
        }
        
        // 随机选择敌人攻击
        const randomIndex = Math.floor(Math.random() * aliveEnemies.length);
        const attacker = aliveEnemies[randomIndex];
        
        // 计算伤害
        const damageInfo = this.calculateEnemyDamage(attacker.enemyData, player);
        
        DebugHelper.log(`${attacker.enemyData.name} 攻击，伤害: ${damageInfo.damage}`);
        
        // 回调攻击完成
        if (onAttackComplete) {
            try {
                onAttackComplete(attacker, damageInfo.damage);
            } catch (error) {
                DebugHelper.error('BattleLogic: 攻击完成回调执行失败', error);
            }
        }
    }

    processStatusEffects(target) {
        if (!target) return 0;
        
        let totalDamage = 0;
        
        // 燃烧伤害
        if (target.burning && target.burningTurns > 0) {
            const burnDamage = Math.floor(target.maxHp * 0.05); // 每回合5%最大生命值
            target.hp -= burnDamage;
            target.burningTurns--;
            totalDamage += burnDamage;
            
            DebugHelper.log(`燃烧伤害: ${burnDamage}, 剩余回合: ${target.burningTurns}`);
            
            if (target.burningTurns <= 0) {
                target.burning = false;
            }
        }
        
        // 中毒伤害
        if (target.poisoned && target.poisonTurns > 0) {
            const poisonDamage = Math.floor(target.maxHp * 0.03); // 每回合3%最大生命值
            target.hp -= poisonDamage;
            target.poisonTurns--;
            totalDamage += poisonDamage;
            
            DebugHelper.log(`中毒伤害: ${poisonDamage}, 剩余回合: ${target.poisonTurns}`);
            
            if (target.poisonTurns <= 0) {
                target.poisoned = false;
            }
        }
        
        // 减速效果
        if (target.slowed && target.slowedTurns > 0) {
            target.slowedTurns--;
            DebugHelper.log(`目标被减速，剩余回合: ${target.slowedTurns}`);
            if (target.slowedTurns <= 0) {
                target.slowed = false;
            }
        }
        
        return totalDamage;
    }

    canAttack(target) {
        // 检查目标是否可以被攻击
        if (!target) return false;
        if (!target.visible) return false;
        if (target.enemyData.hp <= 0) return false;
        if (target.frozen) return false; // 被冻结不能攻击
        
        return true;
    }
    
    /**
     * 检查道具是否可以在战斗中使用
     * @param {Object} item - 道具对象
     * @param {Object} player - 玩家对象
     * @returns {boolean} 是否可以使用
     */
    canUseItemInBattle(item, player) {
        if (!item || !player) return false;
        
        // 只允许消耗品在战斗中使用
        if (item.type !== 'consumable') return false;
        
        // 恢复类道具：检查是否需要恢复
        if (item.healAmount) {
            return player.hp < player.maxHp;
        }
        
        // 修复类道具：检查是否有战车且需要修复
        if (item.repairAmount) {
            return player.vehicle && 
                   player.vehicle.currentChassisHp < player.vehicle.chassisHp;
        }
        
        // 伤害道具：可以在战斗中使用
        if (item.damage) {
            return true;
        }
        
        // 其他消耗品
        return true;
    }
    
    /**
     * 计算技能伤害
     * @param {Object} skill - 技能对象
     * @param {Object} player - 玩家对象
     * @param {Object} target - 目标对象（可选，治疗技能不需要）
     * @returns {Object} 技能效果 {damage?: number, heal?: number, effect?: string}
     */
    calculateSkillEffect(skill, player, target = null) {
        if (!skill || !player) {
            return { damage: 0, heal: 0 };
        }
        
        const result = {
            damage: 0,
            heal: 0,
            effect: skill.effect || null
        };
        
        // 伤害技能
        if (skill.damage) {
            const baseDamage = skill.damage;
            const playerAttack = player.attack || 0;
            
            // 技能伤害 = 基础伤害 + 玩家攻击力加成
            let damage = baseDamage + Math.floor(playerAttack * 0.5);
            
            // 如果有目标，计算防御
            if (target && target.defense) {
                damage = Math.max(this.config.minDamage, damage - target.defense);
            }
            
            // 伤害浮动
            const variation = 0.9 + Math.random() * 0.2; // 90%-110%
            damage = Math.floor(damage * variation);
            
            result.damage = damage;
        }
        
        // 治疗技能
        if (skill.heal) {
            const baseHeal = skill.heal;
            const playerMaxHp = player.maxHp || 50;
            
            // 治疗量 = 基础治疗 + 最大HP的百分比
            let heal = baseHeal + Math.floor(playerMaxHp * 0.1);
            
            // 确保不超过最大HP
            const currentHp = player.hp || 0;
            heal = Math.min(heal, playerMaxHp - currentHp);
            
            result.heal = heal;
        }
        
        return result;
    }
    
    /**
     * 检查是否可以释放技能
     * @param {Object} skill - 技能对象
     * @param {Object} player - 玩家对象
     * @returns {boolean} 是否可以释放
     */
    canUseSkill(skill, player) {
        if (!skill || !player) return false;
        
        // 检查Cost
        const skillCost = skill.cost || 0;
        const playerCost = player.cost || 0;
        
        if (playerCost < skillCost) {
            return false;
        }
        
        // 治疗技能：检查是否需要治疗
        if (skill.heal && player.hp >= player.maxHp) {
            return false;
        }
        
        return true;
    }
    
    /**
     * 消耗技能Cost
     * @param {Object} skill - 技能对象
     * @param {Object} player - 玩家对象
     * @returns {boolean} 是否成功消耗
     */
    consumeSkillCost(skill, player) {
        if (!this.canUseSkill(skill, player)) {
            return false;
        }
        
        const skillCost = skill.cost || 0;
        player.cost = Math.max(0, (player.cost || 0) - skillCost);
        
        return true;
    }

    getBattleStats() {
        const player = this.scene.battlePlayer ? this.scene.battlePlayer.getPlayer() : null;
        const aliveEnemies = this.scene.battleEnemy ? this.scene.battleEnemy.getAliveEnemies() : [];
        
        return {
            playerHP: player ? player.hp : 0,
            playerMaxHP: player ? player.maxHp : 0,
            enemiesAlive: aliveEnemies.length,
            enemiesTotal: this.enemies.length,
            turnCount: this.turnCount || 0
        };
    }

    reset() {
        this.enemies = [];
        this.turnCount = 0;
    }

    destroy() {
        this.events.removeAllListeners();
    }
}